import React, { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import axios from "axios";
import { CheckCircle2, Download, Loader2, Tv, Video } from "lucide-react";
import { ACTIVE_CUSTOMER_ID_CHANGED_EVENT } from "../../hooks/useMerchantStores";
import API_BASE from "../../config/api";
import { downloadVideoFromUrl, getActiveCustomerId, publishVideoToMode } from "./imageCreationService";

// Video Download — paste a YouTube, Instagram or direct video link (.mp4 / .webm / .mov). The server downloads it,
// the merchant previews it here, then adds it to one of their Modes (screens). Results live in this card only.
// The merchant must confirm they have the right to use the video; the server keeps a log of every download.

const MAX_TITLE = 120;

const inputCls =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-50";

// Modes are served by Backend/controllers/modeController.js (/getModes), outside the merchantAuth-protected routes,
// the same way the Image Creation card loads them.
const fetchModesForActiveStore = async () => {
  const customerId = getActiveCustomerId();
  if (!customerId) throw new Error("No store selected. Please sign in again.");
  const { data } = await axios.get(`${API_BASE}/getModes`, { params: { customer_id: customerId } });
  return data?.data || [];
};

const formatSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const SOURCE_LABEL = { youtube: "YouTube", instagram: "Instagram", direct: "Direct link" };

export default function VideoDownload({ onRemove, onVideoReady }) {
  const [url, setUrl] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [video, setVideo] = useState(null); // { downloadId, url, size, contentType, source }

  const [modes, setModes] = useState([]);
  const [modesLoading, setModesLoading] = useState(false);
  const [modeError, setModeError] = useState("");
  const [selectedModeId, setSelectedModeId] = useState("");
  const [title, setTitle] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [publishedTo, setPublishedTo] = useState([]); // mode names the current video was added to

  const loadModes = useCallback(() => {
    setModesLoading(true);
    setModeError("");
    fetchModesForActiveStore()
      .then((list) => setModes(Array.isArray(list) ? list : []))
      .catch((err) => setModeError(err.message || "Failed to load modes."))
      .finally(() => setModesLoading(false));
  }, []);

  // A different store means different Modes and a different library; start clean.
  useEffect(() => {
    const reset = () => {
      setVideo(null);
      setError("");
      setModes([]);
      setSelectedModeId("");
      setPublishedTo([]);
    };
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, reset);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, reset);
  }, []);

  const canDownload = url.trim().length > 0 && confirmed && !downloading;

  const handleDownload = async () => {
    if (!canDownload) return;
    setDownloading(true);
    setError("");
    setVideo(null);
    setPublishedTo([]);
    setSelectedModeId("");
    try {
      const result = await downloadVideoFromUrl({ url: url.trim(), confirmOwnership: true });
      setVideo(result);
      setTitle("");
      onVideoReady?.({ source: result.source, size: result.size });
      loadModes();
    } catch (err) {
      setError(err.message || "Couldn't download that video.");
    } finally {
      setDownloading(false);
    }
  };

  const handlePublish = async () => {
    if (!video || !selectedModeId || publishing) return;
    setPublishing(true);
    setModeError("");
    try {
      await publishVideoToMode({ downloadId: video.downloadId, modeId: selectedModeId, title: title.trim() });
      const modeName = modes.find((m) => m._id === selectedModeId)?.modeName || "Mode";
      setPublishedTo((prev) => (prev.includes(modeName) ? prev : [...prev, modeName]));
      setSelectedModeId("");
      message.success(`Video added to ${modeName}.`);
    } catch (err) {
      setModeError(err.message || "Failed to add the video to the Mode.");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <article className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-rose-50 text-rose-600">
            <Video size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">Video Download</h3>
            <p className="text-xs text-gray-500">Download a video from a link and add it to a Mode</p>
          </div>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={downloading || publishing}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-400 disabled:opacity-50"
          >
            Remove from Components
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* ---------- Inputs ---------- */}
        <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:p-5 lg:border-b-0 lg:border-r">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-gray-900">Video link</span>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleDownload()}
              placeholder="https://www.youtube.com/watch?v=… or https://www.instagram.com/reel/…"
              disabled={downloading}
              className={inputCls}
            />
            <span className="text-xs text-gray-500">YouTube, Instagram, or a direct .mp4 / .webm / .mov link. Up to 100 MB and 5 minutes.</span>
          </label>

          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={downloading} className="mt-0.5 h-4 w-4 rounded border-gray-300" />
            <span>I have the right to use this video.</span>
          </label>

          {error && (
            <p role="alert" className="m-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={handleDownload}
            disabled={!canDownload}
            className="inline-flex w-fit items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            {downloading ? "Downloading…" : "Download video"}
          </button>
          {downloading && <p className="m-0 text-xs text-gray-500">This can take up to a minute for longer videos. Keep this page open.</p>}
        </div>

        {/* ---------- Result ---------- */}
        <div className="flex flex-col gap-4 p-4 sm:p-5">
          {!video ? (
            <div className="grid min-h-[160px] place-items-center rounded-xl border-2 border-dashed border-gray-200 px-4 text-center text-sm text-gray-500">
              Your downloaded video appears here.
            </div>
          ) : (
            <>
              <video src={video.url} controls preload="metadata" className="max-h-72 w-full rounded-lg bg-black" data-testid="video-preview" />
              <p className="m-0 text-xs text-gray-500">
                {SOURCE_LABEL[video.source] || "Video"} · {formatSize(video.size)}
              </p>

              <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900">
                  <Tv size={15} /> Add to Mode
                </span>
                {modeError && <p className="m-0 text-sm text-red-700">{modeError}</p>}
                {modesLoading ? (
                  <p className="m-0 flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 size={14} className="animate-spin" /> Loading your Modes…
                  </p>
                ) : modes.length === 0 ? (
                  <p className="m-0 text-sm text-gray-500">
                    No Modes found. Create one on the Modes page first.{" "}
                    <button type="button" onClick={loadModes} className="font-semibold underline">Refresh</button>
                  </p>
                ) : (
                  <>
                    <select value={selectedModeId} onChange={(e) => setSelectedModeId(e.target.value)} disabled={publishing} className={inputCls} aria-label="Mode">
                      <option value="">Choose a Mode…</option>
                      {modes.map((m) => (
                        <option key={m._id} value={m._id}>{m.modeName}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={title}
                      maxLength={MAX_TITLE}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Title (optional)"
                      disabled={publishing}
                      className={inputCls}
                    />
                    <button
                      type="button"
                      onClick={handlePublish}
                      disabled={!selectedModeId || publishing}
                      className="inline-flex w-fit items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                    >
                      {publishing && <Loader2 size={14} className="animate-spin" />}
                      {publishing ? "Adding…" : "Add to Mode"}
                    </button>
                  </>
                )}
                {publishedTo.length > 0 && (
                  <p className="m-0 inline-flex items-center gap-1.5 text-sm font-medium text-green-700">
                    <CheckCircle2 size={15} /> Added to {publishedTo.join(", ")}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </article>
  );
}
